import { useState } from 'react';
import { Drawer } from 'antd';
import {
    GetTransactionsParams,
    Transactions
} from '../../../interfaces/Transactions';
import { queryConfig } from '../../../api/queryBase';
import { openNotification, validateEmail } from '../../../utils/helper';

import Button from '../../../components/button/Button';
import { CustomRadio } from '../../../components/radio/Radio';
import TextArea from '../../../components/input/TextArea';
import { CloseIcon } from '../../../components/icons';

const exportOptions = [
    {
        label: 'CSV',
        subText: 'Download your file directly to your computer',
        value: 'CSV'
    },
    {
        label: 'Send to mail',
        subText: 'Send the file to your email',
        value: 'MAIL'
    }
];

export const ExportTransactions = ({
    open,
    onClose,
    params
}: {
    open: boolean;
    onClose: () => void;
    params: GetTransactionsParams;
}) => {
    const [exportType, setExportType] = useState('');
    const [emailList, setEmailList] = useState<string>('');

    const query = queryConfig();
    const downloadQuery = queryConfig({ isBlob: true });

    const {
        isLoading: isDownloadTransactionsLoading,
        isFetching: isDownloadTransactionsFetching,
        refetch: downloadTransaction
    } = downloadQuery<Blob>({
        url: '/wallet/transaction/download',
        cacheKey: `transactions-download`,
        params: { serviceable: false },
        enabled: false
    });

    const {
        isLoading: isExportTransactionsLoading,
        isFetching: isExportTransactionsFetching,
        refetch: exportTransactions
    } = query<{
        content: Transactions;
    }>({
        url: '/wallet/transaction/notify',
        cacheKey: `transactions-notify`,
        params: {
            ...params,
            exportEmails: emailList.replace(/ /g, '')
        },
        enabled: false
    });

    const validateEmailList = () => {
        const emails = emailList.replace(/ /g, '').split(',');

        let valid = true;

        for (let i = 0; i < emails.length; i++) {
            if (!validateEmail(emails[i])) {
                valid = false;
            }
        }

        return valid;
    };

    const handleDownload = async () => {
        try {
            const { data } = await downloadTransaction();

            if (data) {
                const newFileUrl = window.URL.createObjectURL(data.payload);
                const link = document.createElement('a');
                link.href = newFileUrl;
                link.setAttribute('download', 'Transactions');
                document.body.appendChild(link);
                link.click();
                onClose();
            }
        } catch (error) {
            openNotification('error', 'Export, Something went wrong');
        }
    };

    const handleMailExport = async () => {
        try {
            await exportTransactions();
            openNotification('info', 'Export, completed Successfully');
            onClose();
        } catch (e) {
            openNotification('error', 'Export, Something went wrong');
        }
    };

    return (
        <Drawer
            placement='right'
            closable={false}
            onClose={() => onClose()}
            open={open}
            width={400}
        >
            <div className='mt-[56px]  px-6 lg:w-[400px]'>
                <div className='flex justify-between items-center mb-4'>
                    <h4 className='text-ebony text-lg font-semibold'>
                        Export file
                    </h4>
                    <button onClick={onClose}>
                        <CloseIcon />
                    </button>
                </div>

                <p className='text-black font-medium text-sm mb-4'>
                    Please select how you’d like to export your file
                </p>

                <div className='flex flex-col gap-4 mt-[24px]'>
                    {exportOptions?.map((item) => (
                        <CustomRadio
                            key={item.value}
                            selected={exportType}
                            setSelected={setExportType}
                            {...item}
                            disabled={
                                isExportTransactionsFetching ||
                                isExportTransactionsLoading ||
                                isDownloadTransactionsFetching ||
                                isDownloadTransactionsLoading
                            }
                        />
                    ))}

                    {exportType === 'MAIL' && (
                        <div>
                            <TextArea
                                label='Enter email'
                                value={emailList}
                                rows={5}
                                onChange={(e) =>
                                    setEmailList(e.currentTarget.value)
                                }
                            />
                            <p>
                                You can enter as many as possible. Use comma to
                                separate
                            </p>
                        </div>
                    )}

                    {exportType && (
                        <Button
                            text='Download'
                            className='w-full mt-4'
                            onClick={
                                exportType === 'CSV'
                                    ? handleDownload
                                    : exportType === 'MAIL'
                                    ? handleMailExport
                                    : () => {}
                            }
                            disabled={
                                (exportType === 'MAIL' && !emailList) ||
                                (exportType === 'MAIL' && !validateEmailList())
                            }
                            isLoading={
                                isExportTransactionsFetching ||
                                isExportTransactionsLoading ||
                                isDownloadTransactionsFetching ||
                                isDownloadTransactionsLoading
                            }
                        />
                    )}
                </div>
            </div>
        </Drawer>
    );
};
